import { About, Contact, Featured, Hero, Jobs, Layout } from '@components';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import Blogs from '../components/sections/blogs';
import Services from '../components/sections/services';
import Workstation from '../components/sections/workstation';

const StyledMainContainer = styled.main`
  display: flex;
  flex-direction: column;
  gap: 100px;
  align-items: center;
  width: 100%;
  max-width: 1250px;
  margin: 0 auto;
  padding: 0 50px 100px;

  @media (max-width: 768px) {
    padding: 0 10px 50px;
    gap: 60px;
  }

  section {
    width: 100%;
    margin: 0 auto;
    padding: 0;
  }
`;

// Module-scoped global variables to persist scroll state across unmounts/route transitions
const sectionScrollPositions = {};
let isNavbarClick = false;

const IndexPage = ({ location }) => {
  const [hash, setHash] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.location.hash;
    }
    return location.hash || '';
  });

  // Set history scroll restoration to manual on mount to prevent browser scroll fighting
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Sync hash directly from Gatsby's Reach Router location prop (primary source of truth)
    if (location && location.hash !== undefined) {
      setHash(location.hash);
    } else if (typeof window !== 'undefined') {
      setHash(window.location.hash);
    }

    const handleHashChange = () => {
      setHash(window.location.hash);
    };

    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [location]);

  const showWorkstation = hash === '#workstation' || hash === '#/workstation';

  // Record home page scroll position continuously when workstation is NOT active
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        const currentHash = window.location.hash || '';
        const isWorkstation = currentHash === '#workstation' || currentHash === '#/workstation';
        if (!showWorkstation && !isWorkstation && window.scrollY > 0) {
          sectionScrollPositions[currentHash] = window.scrollY;
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [showWorkstation]);

  // Listen for clicks on navbar links to flag explicit navigation and enforce perfect scroll centering
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleNavbarClick = event => {
      const link = event.target.closest('a');
      if (link) {
        const href = link.getAttribute('href') || '';
        if (href.startsWith('/#') || href.startsWith('#')) {
          const targetId = href.split('#')[1];
          if (targetId) {
            const el = document.getElementById(targetId);
            if (el) {
              event.preventDefault(); // Prevents browser default top-aligned jump/smooth scroll
              isNavbarClick = true;

              const isShortScreen = typeof window !== 'undefined' && window.innerHeight <= 600;
              const shouldCenter = (targetId === 'jobs' || targetId === 'blogs') && !isShortScreen;
              el.scrollIntoView({
                behavior: 'smooth',
                block: shouldCenter ? 'center' : 'start',
              });

              // Update hash in URL quietly without jumping
              window.history.pushState(null, null, `#${targetId}`);
              setHash(`#${targetId}`);

              setTimeout(() => {
                isNavbarClick = false;
              }, 100);
            }
          }
        } else if (href === '/' || href === '/#') {
          isNavbarClick = true;
          setTimeout(() => {
            isNavbarClick = false;
          }, 100);
        }
      }
    };

    window.addEventListener('click', handleNavbarClick, { capture: true });
    return () => window.removeEventListener('click', handleNavbarClick, { capture: true });
  }, []);

  // Synchronously restore scroll position before browser paint and ScrollReveal initializations run
  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      if (showWorkstation) {
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, 0);
      } else {
        const currentHash = window.location.hash || '';
        const savedScroll = sectionScrollPositions[currentHash] || 0;
        if (!isNavbarClick) {
          document.documentElement.style.scrollBehavior = 'auto';
          window.scrollTo(0, savedScroll);
        }
      }
    }
  }, [showWorkstation]);

  // Restore scroll position when leaving workstation view to the previous section
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentHash = window.location.hash || '';
      const savedScroll = sectionScrollPositions[currentHash] || 0;

      if (!showWorkstation && !isNavbarClick) {
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, savedScroll);

        const timer1 = setTimeout(() => {
          window.scrollTo(0, savedScroll);
        }, 50);

        const timer2 = setTimeout(() => {
          window.scrollTo(0, savedScroll);
        }, 150);

        const timer3 = setTimeout(() => {
          window.scrollTo(0, savedScroll);
        }, 300);

        const timer4 = setTimeout(() => {
          window.scrollTo(0, savedScroll);
          document.documentElement.style.scrollBehavior = '';
        }, 600);

        return () => {
          clearTimeout(timer1);
          clearTimeout(timer2);
          clearTimeout(timer3);
          clearTimeout(timer4);
          document.documentElement.style.scrollBehavior = '';
        };
      } else if (!showWorkstation) {
        document.documentElement.style.scrollBehavior = '';
      }
    }
  }, [showWorkstation, hash]);

  // Ensure the viewport is at the top when showing the Workstation view
  useEffect(() => {
    if (typeof window !== 'undefined' && showWorkstation) {
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);

      const t1 = setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);
      const t2 = setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.style.scrollBehavior = '';
      }, 150);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        document.documentElement.style.scrollBehavior = '';
      };
    }
    return undefined;
  }, [showWorkstation]);

  // Handle smooth scroll when navigating back to the homepage sections from Workstation view
  useEffect(() => {
    if (typeof window !== 'undefined' && !showWorkstation && hash) {
      const currentHash = hash.replace('#/', '#');
      const savedScroll = sectionScrollPositions[currentHash] || 0;

      // If we have a saved scroll position and are returning to the same section,
      // skip the scrollIntoView animation so the exact scroll position is restored perfectly.
      const shouldRestoreScroll = savedScroll > 0 && !isNavbarClick;

      if (shouldRestoreScroll) {
        return undefined;
      }

      const targetId = hash.replace('#/', '').replace('#', '');
      if (targetId) {
        const scrollToElement = () => {
          const el = document.getElementById(targetId);
          if (el) {
            const isShortScreen = typeof window !== 'undefined' && window.innerHeight <= 600;
            const shouldCenter = (targetId === 'jobs' || targetId === 'blogs') && !isShortScreen;
            el.scrollIntoView({
              behavior: 'smooth',
              block: shouldCenter ? 'center' : 'start',
            });
          }
        };

        // Always run immediate AND progressive scroll operations to absorb layout/height shifts during hydration
        scrollToElement();
        const t1 = setTimeout(scrollToElement, 50);
        const t2 = setTimeout(scrollToElement, 150);
        const t3 = setTimeout(scrollToElement, 300);
        const t4 = setTimeout(scrollToElement, 600);

        return () => {
          clearTimeout(t1);
          clearTimeout(t2);
          clearTimeout(t3);
          clearTimeout(t4);
        };
      }
    }
    return undefined;
  }, [showWorkstation, hash]);

  return (
    <Layout location={location}>
      <StyledMainContainer
        className="fillHeight"
        style={{ gap: showWorkstation ? '0' : undefined }}
      >
        {!showWorkstation && (
          <>
            <Hero />
            <About />
            <Services />
            <Jobs />
            <Featured />
            <Blogs />
            <Contact />
          </>
        )}
        <div style={{ display: showWorkstation ? 'block' : 'none', width: '100%' }}>
          <Workstation showWorkstation={showWorkstation} />
        </div>
      </StyledMainContainer>
    </Layout>
  );
};

IndexPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default IndexPage;
